

// "use client"

import React, { useState, useEffect, useMemo } from "react"
import { createPortal } from "react-dom"
import axios from "../../../../lib/axios"
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "components/ui/table"
import {
  Sheet,
  SheetContent,
  SheetClose,
} from "components/ui/sheet"
import ProductDetails from "./ProductDetails"
import { useInterval } from "contexts/IntervalContext"
// import {
//   DropdownMenu,
//   DropdownMenuTrigger,
//   DropdownMenuContent,
//   DropdownMenuItem,
// } from "components/ui/dropdown-menu"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "components/ui/dialog"
import { Input } from "components/ui/input"
import { Button } from "components/ui/button"
import {
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  Plus,
} from "lucide-react"

export default function ProductTable() {
  const [products, setProducts] = useState([])
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [isOpen, setIsOpen] = useState(false)
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" })
  const [filter, setFilter] = useState("all")
  const [page, setPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState("")
  const [dialogOpen, setDialogOpen] = useState(false)
  const { interval } = useInterval() 

  // 새 상품 데이터
  const [newProduct, setNewProduct] = useState({
    name: "",
    category: "",
    price: "",
    stock: "",
  })
  // 🔧 NEW: 테이블 컬럼 정의 (Header + Body 공통)
 const COLUMN_KEYS = [
  "name",
  "price",
  "trend_rate",
  "forecast_signal",
  "revenue_ratio",
  "inventory_risk",
  "action_hint",   // 🔧 NEW
]
  const rowsPerPage = products.length

  // ✅ 상품 데이터 로드
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const token = localStorage.getItem("access_token")
        const response = await axios.get(`/api/products/?interval=${interval}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        console.log("📦 [ProductTable] raw response:", response)
        console.log("📦 [ProductTable] response.data:", response.data)

        setProducts(response.data)
      } catch (error) {
        console.error("❌ Failed to fetch products:", error)
      }
    }
    fetchProducts()
  }, [interval])

  // ✅ 필터 + 검색
  const filteredProducts = useMemo(() => {
    let result = [...products]
    if (filter === "synced") result = result.filter(p => p.status === "done")
    if (filter === "pending") result = result.filter(p => p.status !== "done")
    if (searchQuery.trim()) {
      result = result.filter((p) =>
        p.name?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    }
    return result
  }, [filter, products, searchQuery])

  // ✅ 정렬
  const sortedProducts = useMemo(() => {
    const sorted = [...filteredProducts]
    if (sortConfig.key) {
      sorted.sort((a, b) => {
        const aVal = a[sortConfig.key] ?? ""
        const bVal = b[sortConfig.key] ?? ""
        if (typeof aVal === "string") {
          return sortConfig.direction === "asc"
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal)
        } else {
          return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal
        }
      })
    }
    return sorted
  }, [filteredProducts, sortConfig])

  // ✅ 페이지 처리
  const startIndex = (page - 1) * rowsPerPage
  const paginatedProducts = sortedProducts.slice(startIndex, startIndex + rowsPerPage)
  const totalPages = Math.ceil(sortedProducts.length / rowsPerPage)

  const handleSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    )
  }

  const handleRowClick = (product) => {
    setSelectedProduct(product)
    setIsOpen(true)
  }

  const handleAddProduct = () => {
    console.log("✅ New product:", newProduct)
    setDialogOpen(false)
    setNewProduct({ name: "", category: "", price: "", stock: "" })
    // ⚠️ TODO: axios.post("/api/products/", newProduct)
  }

  return (
    <div className="rounded-2xl border border-neutral-800 bg-neutral-950 p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6">
        {/* Left: Filter */}
        <div className="inline-flex bg-neutral-900/60 border border-neutral-800 rounded-lg p-1">
          {["all", "synced", "pending"].map((type) => (
            <button
              key={type}
              onClick={() => {
                setFilter(type)
                setPage(1)
              }}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                filter === type
                  ? "bg-neutral-800 text-white"
                  : "text-neutral-400 hover:text-white"
              }`}
            >
              {type === "all"
                ? "All"
                : type === "synced"
                ? "Synced"
                : "Pending"}
            </button>
          ))}
        </div>

        {/* Center: Search */}
        <div className="flex-1 w-full md:w-1/3">
          <Input
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bg-neutral-900 border-neutral-800 text-white placeholder:text-neutral-500"
          />
        </div>

        {/* Right: Add Product */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-yellow-500 hover:bg-yellow-400 text-black font-medium">
              <Plus className="w-4 h-4 mr-2" /> Add Product
            </Button>
          </DialogTrigger>

          <DialogContent className="bg-neutral-950 border border-neutral-800 text-white sm:max-w-[480px]">
            <DialogHeader>
              <DialogTitle>Add New Product</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <Input
                placeholder="Product name"
                value={newProduct.name}
                onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                className="bg-neutral-900 border-neutral-800"
              />
              <Input
                placeholder="Category"
                value={newProduct.category}
                onChange={(e) => setNewProduct({ ...newProduct, category: e.target.value })}
                className="bg-neutral-900 border-neutral-800"
              />
              <Input
                placeholder="Price"
                value={newProduct.price}
                onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })}
                className="bg-neutral-900 border-neutral-800"
              />
              <Input
                placeholder="Stock"
                value={newProduct.stock}
                onChange={(e) => setNewProduct({ ...newProduct, stock: e.target.value })}
                className="bg-neutral-900 border-neutral-800"
              />
            </div>
            <DialogFooter>
              <Button onClick={handleAddProduct} className="bg-yellow-500 text-black font-medium">
                Add
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-neutral-800 bg-neutral-900/40 overflow-hidden">
        <Table>
          <TableHeader className="bg-neutral-900/60">
            <TableRow>
             {/* {["name", "category", "source","status", "price", "stock", "last_synced"].map((key) => ( */}

              {[
                  "name",
                  "price",

                  // 🔧 NEW
                  "trend_rate",
                  "forecast_signal",
                  "revenue_ratio",
                  "inventory_risk",
                  "action_hint",

                ].map((key) => (

                <TableHead
                  key={key}
                  onClick={() => handleSort(key)}
                  className="cursor-pointer select-none text-neutral-400"
                >
                  {/* {key === "name"
                    ? "Name"
                    : key === "category"
                    ? "Category"
                    : key === "source"
                    ? "Source"
                    : key === "status"
                    ? "Status"
                    : key === "price"
                    ? "Price"
                    : key === "stock"
                    ? "Stock"
                    : "Last Synced"}{" "} */}
                    {key === "trend_rate"
                    ? "Trend"
                    : key === "forecast_signal"
                    ? "Forecast"
                    : key === "revenue_ratio"
                    ? "Revenue %"
                    : key === "inventory_risk"
                    ? "Inventory"
                    : key === "action_hint"
                    ? "Action"
                    : key === "name"
                    ? "Product"
                    : key === "price"
                    ? "Price"
                    : key}
                  {sortConfig.key === key &&
                    (sortConfig.direction === "asc" ? "↑" : "↓")}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>

          <TableBody>
            {paginatedProducts.map((p) => (
             
                // <TableRow
                //   key={p.id}
                //   className="hover:bg-neutral-800/50 transition-all duration-200 cursor-pointer border-neutral-800"
                //   onClick={() => handleRowClick(p)}
                // > 
                <TableRow
                    key={p.id}
                    className={`cursor-pointer transition-all duration-200 border-neutral-800
                      ${
                        p.inventory_risk === "high"
                          ? "bg-red-500/5 hover:bg-red-500/10"
                          : p.inventory_risk === "medium"
                          ? "bg-yellow-500/5 hover:bg-yellow-500/10"
                          : "hover:bg-neutral-800/50"
                      }
                    `}
                    onClick={() => handleRowClick(p)}
                  >
                  {COLUMN_KEYS.map((key) => (
                    <TableCell key={key}>
                      {key === "trend_rate" ? (
                        p.trend_rate == null ? (
                           <span className="text-neutral-500">—</span>
                        ) : (
                            <span
                              className={`flex items-center gap-1 text-sm font-medium ${
                                p.trend_rate > 0
                                  ? "text-green-400"
                                  : "text-red-400"
                              }`}
                            >
                              {p.trend_rate > 0 ? "↑" : "↓"}
                              {Math.abs(p.trend_rate)}%
                            </span>
                          )


                      ) : key === "forecast_signal" ? (
                          <span
                            className={`px-2 py-1 rounded text-xs font-medium inline-flex items-center gap-1 ${
                              p.forecast_signal === "up"
                                ? "bg-green-500/10 text-green-400"
                                : p.forecast_signal === "down"
                                ? "bg-red-500/10 text-red-400"
                                : "bg-neutral-500/10 text-neutral-300"
                            }`}
                          >
                            {p.forecast_signal === "up" && "📈 Demand up"}
                            {p.forecast_signal === "down" && "📉 Demand down"}
                            {!p.forecast_signal && "→ Stable"}
                          </span>
                      ) : key === "revenue_ratio" ? (
                        p.revenue_ratio != null ? `${p.revenue_ratio}%` : "-"
                    ) : key === "inventory_risk" ? (
                      <span
                        className={`px-2 py-1 rounded text-xs font-semibold ${
                          p.inventory_risk === "high"
                            ? "bg-red-500/15 text-red-400"
                            : p.inventory_risk === "medium"
                            ? "bg-yellow-500/15 text-yellow-400"
                            : "bg-green-500/15 text-green-400"
                        }`}
                      >
                        {p.inventory_risk === "high" && "⚠ High risk"}
                        {p.inventory_risk === "medium" && "⚠ Medium"}
                        {p.inventory_risk === "low" && "✓ OK"}
                      </span>
                     ) : key === "action_hint" ? (
                        <div className="flex flex-col leading-tight">
                          <span
                            className={`font-medium underline cursor-pointer ${
                              p.action_hint === "Restock"
                                ? "text-yellow-400"
                                : p.action_hint === "Review"
                                ? "text-red-400"
                                : "text-neutral-400"
                            }`}
                          >
                            {p.action_hint}
                          </span>

                          {p.profit_impact > 0 && (
                            <span className="text-xs text-neutral-400">
                              {p.profit_impact_type === "prevent_loss"
                                ? `Prevent $${p.profit_impact} loss`
                                : `+$${p.profit_impact} potential`}
                            </span>
                          )}
                        </div>
                      ) : (
                        p[key] ?? "-"
                        
                      )}
                      
                    </TableCell>
                  ))}
                </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex justify-between items-center mt-4 text-sm text-neutral-400">
        <span>
          Rows per page: <strong>{rowsPerPage}</strong>
        </span>
        <div className="flex items-center gap-3">
          <span>
            Page {page} of {totalPages}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="text-neutral-400 hover:text-white"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              disabled={page === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="text-neutral-400 hover:text-white"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Sheet */}
      {isOpen &&
        createPortal(
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetContent
                  side="right"
                  className="fixed inset-y-0 right-0 z-[9050] flex flex-col text-white shadow-xl
                            backdrop-blur-lg bg-neutral-950/100 will-change-transform
                            w-[500px] sm:w-[750px] lg:w-[820px] border-l border-neutral-800 !p-0"
                >
              <div className="flex flex-col h-full">

                <div className="px-6 pt-6 pb-4 flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-white">
                    {selectedProduct?.name}
                  </h3>
                  <SheetClose asChild>
                    <button
                      onClick={() => setIsOpen(false)}
                      className="text-gray-400 hover:text-white transition"
                    >
                      ✕
                    </button>
                  </SheetClose>
                </div>

                <div className="flex-1 overflow-y-auto no-scrollbar px-1 space-y-6">
                  <ProductDetails itemId={selectedProduct?.id} isOpen={isOpen} />
                </div>
              </div>
            </SheetContent>
          </Sheet>,
          document.body
        )}
    </div>
  )
}